import React, { useState } from "react";

import { graphql } from "relay-runtime";

import { getIntegrationForKey } from "~/integrations/actionItemIntegration";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { microsoftTodoKey } from "~/settings/Integrations/MicrosoftTodo/MicrosoftTodoIntegration";
import Checkbox from "~/ui/legacy/Checkbox";
import { Input } from "~/ui/legacyFellowIndex";

import type { CreateOrImportProps } from "../actionItemIntegration/actionItemLinkingModal";
import { ListSelector } from "./selectors/MicrosoftTodoListSelector";

import type { MicrosoftTodoCreateNewExternalTicketPaneMutation } from "~/__generated__/MicrosoftTodoCreateNewExternalTicketPaneMutation.graphql";

import styles from "./css/styles.module.css";

const createExternalTicket = graphql`
	mutation MicrosoftTodoCreateNewExternalTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$title: String!
		$destinationInfo: JSON!
	) {
		sendActionItemToIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				title: $title
				destinationInfo: $destinationInfo
				integrationProvider: MICROSOFT_TODO
			}
		) {
			clientMutationId
		}
	}
`;

function linkToMicrosoftTodo(
	blockKey: string,
	noteId: string,
	title: string,
	listId: string,
	listName: string,
	important: boolean,
) {
	return commitMutationPromise<MicrosoftTodoCreateNewExternalTicketPaneMutation>(environment, {
		mutation: createExternalTicket,
		variables: {
			blockKey,
			noteId,
			title,
			destinationInfo: {
				/* eslint-disable camelcase */
				list_id: listId,
				list_name: listName,
				important: important,
				/* eslint-enable camelcase */
			},
		},
	});
}

export function CreateNewExternalTicketPane({
	setCanSubmit,
	registerSubmit,
	initialText,
	blockKey,
	noteId,
}: CreateOrImportProps) {
	const [listId, setListId] = useState<string>();
	const [listName, setListName] = useState<string>();
	const [important, setImportant] = useState<boolean>(false);
	const [title, setTitle] = useState<string>(initialText || "");

	const onTitleChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		// @ts-ignore
		setTitle(e.target.value);
	}, []);

	const { displayName } = getIntegrationForKey(microsoftTodoKey);

	const submit = React.useCallback(async () => {
		if (!title || !listId || !listName) {
			throw new Error("Submission validation step failed");
		}
		return linkToMicrosoftTodo(blockKey, noteId, title, listId, listName, important).then(() => true);
	}, [blockKey, noteId, title, listId, listName, important]);

	const onImportantChange = React.useCallback(() => {
		setImportant(!important);
	}, [important]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		setCanSubmit(Boolean(listId && title));
	}, [setCanSubmit, listId, title]);

	return (
		<div className={styles.container}>
			<label className={styles.list}>
				<div className={styles.label}>List</div>
				<ListSelector listId={listId} setListId={setListId} setListName={setListName} />
			</label>
			<label className={styles.createTitle}>
				<div className={styles.label}>Title</div>
				<Input
					name="microsoft_todo_task_title"
					className={styles.titleInput}
					type="text"
					value={title}
					onChange={onTitleChange}
				/>
			</label>
			<label className={styles.important}>
				<Checkbox green checked={important} onChange={onImportantChange} className={styles.checkIcon} />
				<span>Mark as important</span>
			</label>
			<div className={styles.help}>
				The task name, due date, and status will automatically sync with the {displayName} list selected.
			</div>
		</div>
	);
}
